import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, LinkV2, TextV2, useDisclosure } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { Filters } from './Filters';
import { FilterOverflowRow } from './components/FilterOverflowRow';
import { useResponsiveFilterOverflow } from './useResponsiveFilterOverflow';
import type { FilterFacet } from './Filter.types';
import { SearchBar } from '../Search/SearchBar';
import { useIsMobileOrTablet, useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useApplySearchFiltersToTemplates } from '../../useApplySearchFiltersToTemplates';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { animationTransition } from '@shared/utils/animationTransition';
import { Restart } from '@withjoy/joykit/icons';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';
import { useTranslation } from '@shared/core';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { SearchBox } from '../Search/SearchBox';

const NoSearchResultsText = ({ paddingLeft, onClick }: { paddingLeft?: BoxProps['paddingLeft']; onClick?: () => void }) => {
  const { t } = useTranslation('stationery');

  return (
    <TextV2 paddingLeft={paddingLeft}>
      {t('noSearchResults')()}{' '}
      <span>
        <LinkV2
          onClick={onClick}
          textDecoration="none"
        >
          {t('clear')()}
        </LinkV2>
      </span>
    </TextV2>
  );
};

const ResetAllFiltersButton = ({ onClick, pillHeight = 40 }: { onClick: () => void; pillHeight?: number }) => {
  const { t } = useTranslation('stationery');
  const tFiltersDrawer = t('dashboard', 'designsGallery', 'filtersDrawer');

  return (
    <Box
      onClick={onClick}
      backgroundColor="mono2"
      color="mono14"
      as="button"
      flex="none"
      height={pillHeight}
      paddingX={5}
      borderRadius={24}
      transition={animationTransition('background-color')}
    >
      <Box
        as="span"
        display="inline-flex"
        borderRadius="inherit"
        alignItems="center"
        columnGap={3}
        typographyVariant="button1"
      >
        <Restart size={16} />
        <Box
          as="span"
          display={{ _: 'none', sm: 'inline-flex' }}
        >
          {tFiltersDrawer.resetCta()}
        </Box>
      </Box>
    </Box>
  );
};

export const SearchAndFiltersContainer = (props: { filterFacets: FilterFacet[]; trailingAction?: React.ReactNode }) => {
  const { filterFacets, trailingAction } = props;
  const isMobileOrTablet = useIsMobileOrTablet();
  // Matches the breakpoint at which `Filters` swaps individual pills for the single drawer trigger.
  const isMobileScreen = useIsMobileScreen();
  const { buttonInteracted } = useCardTelemetry();
  const { clearAllFilters, getTotalActiveFilterCount } = useDesignGalleryFiltersContext();

  const [isSearchV1Enabled] = useIsFeatureEnabled('printGalleryEnableSearch');
  const [isSearchV2Enabled] = useIsFeatureEnabled('printSearch');
  const [previewWithPhotoEnabled] = useIsFeatureEnabled('printPreviewWithPhotoCards');

  const { useSearchResults, clearSearch } = useApplySearchFiltersToTemplates();
  const searchResults = useSearchResults();
  const { searchError } = searchResults;

  // Facets hidden behind the "More" pill. The expanded overflow row is rendered at the top level
  // (below) so it spans the full width instead of being confined to the filter column.
  const { overflowFacets } = useResponsiveFilterOverflow(filterFacets);
  const { isOpen: isOverflowExpanded, onToggle: toggleOverflow, onClose: collapseOverflow } = useDisclosure();
  useEffect(() => {
    if (overflowFacets.length === 0) {
      collapseOverflow();
    }
  }, [overflowFacets.length, collapseOverflow]);

  const handleClearAllFilters = () => {
    buttonInteracted({ name: 'galleryResetFilters', activeFiltersCount: getTotalActiveFilterCount() });
    clearAllFilters();
  };

  const pillHeight = previewWithPhotoEnabled ? 48 : 40;

  const resetAction = getTotalActiveFilterCount() ? (
    <ResetAllFiltersButton
      onClick={handleClearAllFilters}
      pillHeight={pillHeight}
    />
  ) : undefined;

  const showOverflowRow = !isMobileScreen && isOverflowExpanded && overflowFacets.length > 0;

  // New filter-bar design (Figma) is gated behind the preview-with-photo flag: filters on the left,
  // search + preview button right-aligned, all at a consistent 40px height.
  if (previewWithPhotoEnabled) {
    return (
      <>
        {/* Wrap the two rows in one element so PageSection's column rowGap spaces the whole bar
            from the gallery, not the primary row from its overflow row. */}
        <Box>
          <Flex
            alignItems={{ _: 'center', sm2: 'flex-start' }}
            gap={{ _: 3, sm2: 0 }}
            paddingLeft={{ _: 6, sm2: 6 }}
            paddingRight={{ _: 4, sm2: 14 }}
            flexWrap="wrap"
          >
            {/* On mobile the filter controls sit to the RIGHT of the search bar (order 2);
                on desktop they lead the row (order 0). */}
            <Flex
              order={{ _: 2, sm2: 0 }}
              flex={{ _: 'none', sm2: 1 }}
              paddingY="1px"
              alignItems="flex-start"
              gap={2}
              flexWrap="wrap"
              minWidth={0}
            >
              <Filters
                facets={filterFacets}
                pillHeight={pillHeight}
                overflowExpanded={isOverflowExpanded}
                onToggleOverflow={toggleOverflow}
              />
              {!isMobileOrTablet && searchError && <NoSearchResultsText onClick={clearSearch} />}
              {resetAction}
            </Flex>
            {(isSearchV1Enabled || isSearchV2Enabled || (trailingAction && !isMobileOrTablet)) && (
              <Flex
                order={{ _: 1, sm2: 0 }}
                flex={{ _: 1, sm2: 'none' }}
                alignItems="center"
                gap={3}
                paddingLeft={{ _: 0, sm2: 3 }}
                minWidth={0}
              >
                {isSearchV2Enabled && <SearchBox compact />}
                {isSearchV1Enabled && <SearchBar />}
                {!isMobileOrTablet && trailingAction}
              </Flex>
            )}
          </Flex>
          {showOverflowRow && (
            <Box
              paddingLeft={{ _: 6, sm2: 6 }}
              paddingRight={{ _: 4, sm2: 14 }}
              paddingTop={2}
            >
              <FilterOverflowRow
                facets={overflowFacets}
                pillHeight={pillHeight}
              />
            </Box>
          )}
        </Box>
        {isMobileOrTablet && searchError && (
          <NoSearchResultsText
            paddingLeft={['1.5rem', '2.5rem']}
            onClick={clearSearch}
          />
        )}
        {isMobileOrTablet && trailingAction && (
          <Flex
            position="fixed"
            bottom={6}
            zIndex="dropdown"
            justifyContent="center"
            width="100%"
            pointerEvents="none"
          >
            <Box pointerEvents="auto">{trailingAction}</Box>
          </Flex>
        )}
      </>
    );
  }

  // Original layout (flag off).
  return (
    <>
      <Box>
        <Flex
          paddingLeft={{ _: 6, sm2: 8 }}
          paddingRight={{ _: 4, sm2: 6 }}
          paddingY="1px"
          alignItems="center"
          gap={{ _: 16, sm2: 24 }}
          flexWrap="wrap"
        >
          {isSearchV2Enabled && <SearchBox />}
          <Filters
            facets={filterFacets}
            overflowExpanded={isOverflowExpanded}
            onToggleOverflow={toggleOverflow}
          />
          {isSearchV1Enabled && <SearchBar />}
          {!isMobileOrTablet && searchError && <NoSearchResultsText onClick={clearSearch} />}
          {resetAction}
        </Flex>
        {showOverflowRow && (
          <Box
            paddingLeft={{ _: 6, sm2: 8 }}
            paddingRight={{ _: 4, sm2: 6 }}
            paddingTop={2}
          >
            <FilterOverflowRow facets={overflowFacets} />
          </Box>
        )}
      </Box>
      {isMobileOrTablet && searchError && (
        <NoSearchResultsText
          paddingLeft={['1.5rem', '2.5rem']}
          onClick={clearSearch}
        />
      )}
    </>
  );
};
